/** Live nav search — memorials + missing & found people, works from any page. */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Flame, UserSearch, TreeDeciduous, Loader2 } from "lucide-react";
import { FAMILY_TREES } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NavSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [memorials, setMemorials] = useState([]);
  const [missing, setMissing] = useState([]);
  const [trees, setTrees] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    function onOutside(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    function onEsc(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) {
      setMemorials([]);
      setMissing([]);
      setTrees([]);
      return;
    }
    const needle = q.trim().toLowerCase();
    setTrees(
      FAMILY_TREES.filter(
        (t) =>
          t.name.toLowerCase().includes(needle) ||
          t.lineage.toLowerCase().includes(needle) ||
          t.region.toLowerCase().includes(needle)
      ).slice(0, 4)
    );
    setLoading(true);
    const t = setTimeout(() => {
      Promise.allSettled([
        axios.get(`${API}/v1/memorials/search`, { params: { q: q.trim(), limit: 4 } }),
        axios.get(`${API}/v1/missing`, { params: { q: q.trim(), limit: 4 } }),
      ]).then(([m, c]) => {
        setMemorials(m.status === "fulfilled" ? m.value.data.results || [] : []);
        setMissing(c.status === "fulfilled" ? c.value.data.cases || [] : []);
        setLoading(false);
      });
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const hasResults = memorials.length > 0 || missing.length > 0 || trees.length > 0;

  return (
    <div ref={boxRef} className="relative">
      <button
        data-testid="nav-search-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-label="Search memorials and missing persons"
        className="w-9 h-9 rounded-full flex items-center justify-center border border-[color:var(--ink-line)] text-[color:var(--ink)] hover:bg-[color:var(--parchment-2)] transition-colors"
      >
        {open ? <X size={15} /> : <Search size={15} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="absolute top-full right-0 mt-2 w-[380px] max-w-[92vw] bg-[color:var(--bone-2)] border border-[color:var(--ink-line)] rounded-2xl shadow-2xl overflow-hidden z-50"
            data-testid="nav-search-panel"
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[color:var(--ink-line)]">
              <Search size={15} className="text-[color:var(--ink-soft)] shrink-0" />
              <input
                ref={inputRef}
                data-testid="nav-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search a name, a memorial, a missing person…"
                className="flex-1 bg-transparent text-[14px] focus:outline-none placeholder:text-[color:var(--ink-soft)]/60"
              />
              {loading && <Loader2 size={14} className="animate-spin text-[color:var(--ink-soft)]" />}
            </div>

            <div className="max-h-[60vh] overflow-y-auto" data-testid="nav-search-results">
              {q.trim().length >= 2 && !loading && !hasResults && (
                <p className="px-4 py-5 text-[13px] text-[color:var(--ink-soft)]" data-testid="nav-search-empty">
                  No matches yet. Try a different name.
                </p>
              )}

              {memorials.length > 0 && (
                <div className="py-2">
                  <div className="px-4 py-1 overline text-[color:var(--ink-soft)]">Memorials</div>
                  {memorials.map((r) => {
                    const name = [r.first_name, r.last_name].filter(Boolean).join(" ") || "Unnamed";
                    return (
                      <button
                        key={r.memorial_id}
                        data-testid={`nav-search-memorial-${r.memorial_id}`}
                        onClick={() => {
                          setOpen(false);
                          navigate(`/memorial/${r.memorial_id}`);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[color:var(--parchment-2)] transition-colors"
                      >
                        <Flame size={14} className="text-[color:var(--rust)] shrink-0" />
                        <span className="text-[14px] truncate">{name}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {trees.length > 0 && (
                <div className="py-2 border-t border-[color:var(--ink-line)]">
                  <div className="px-4 py-1 overline text-[color:var(--ink-soft)]">Family Trees</div>
                  {trees.map((t) => (
                    <button
                      key={t.id}
                      data-testid={`nav-search-tree-${t.id}`}
                      onClick={() => {
                        setOpen(false);
                        navigate("/family-trees");
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[color:var(--parchment-2)] transition-colors"
                    >
                      <TreeDeciduous size={14} className="text-[color:var(--sepia-warm)] shrink-0" />
                      <span className="text-[14px] truncate">{t.name}</span>
                    </button>
                  ))}
                </div>
              )}

              {missing.length > 0 && (
                <div className="py-2 border-t border-[color:var(--ink-line)]">
                  <div className="px-4 py-1 overline text-[color:var(--ink-soft)]">Missing &amp; Found</div>
                  {missing.map((c) => (
                    <button
                      key={c.case_id}
                      data-testid={`nav-search-missing-${c.case_id}`}
                      onClick={() => {
                        setOpen(false);
                        navigate(`/missing/case/${c.case_id}`);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[color:var(--parchment-2)] transition-colors"
                    >
                      <UserSearch size={14} className="text-[color:var(--sepia)] shrink-0" />
                      <span className="text-[14px] truncate">{c.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
